import React, { useEffect, useState, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./monthPicker.css";

import AdminLayout from "../../layout/admin";
import { getMethodApiCall } from "../../services/api";
import GenericDataLayout from "../../components/custom/GenericDataLayout";
import GenericTable from "../../components/custom/GenericTable";

const ApprovedAttendance = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ itis: [], trades: [] });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedIti, setSelectedIti] = useState("all");
  const [selectedTrade, setSelectedTrade] = useState("all");

  const selectedYear = String(selectedDate.getFullYear());
  const selectedMonth = String(selectedDate.getMonth() + 1);

  const user = JSON.parse(localStorage.getItem("loginedUser"));
  const industryId = user?.industry?.id;

  const fetchApprovedAttendance = useCallback(async () => {
    if (!industryId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        year: selectedYear,
        month: selectedMonth,
      });
      if (selectedIti !== "all") params.set("iti_id", selectedIti);
      if (selectedTrade !== "all") params.set("trade_id", selectedTrade);

      const url = `/v1/api/industry/approved-attendance/${industryId}?${params.toString()}`;
      const res = await getMethodApiCall(url);
      if (res?.status === 200) {
        setRows(res.data?.data || []);
        setFilterOptions(res.data?.filterOptions || { itis: [], trades: [] });
        setTotalRecords(res.data?.meta?.totalRecords || 0);
        setTotalPages(res.data?.meta?.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching approved attendance:", error);
    } finally {
      setLoading(false);
    }
  }, [industryId, page, limit, selectedYear, selectedMonth, selectedIti, selectedTrade]);

  useEffect(() => {
    fetchApprovedAttendance();
  }, [fetchApprovedAttendance]);

  const handleClearFilters = () => {
    setPage(1);
    setLimit(10);
    setSelectedDate(new Date());
    setSelectedIti("all");
    setSelectedTrade("all");
  };

  const itiOptions = (filterOptions?.itis || []).map((iti) => ({
    label: iti.name,
    value: String(iti.id),
  }));

  const tradeOptions = (filterOptions?.trades || []).map((trade) => ({
    label: trade.name,
    value: String(trade.id),
  }));

  const filtersConfig = [
    {
      type: "custom",
      render: () => (
        <div className="w-full">
          <label className="text-xs font-semibold text-primary mb-1.5 block">Month & Year</label>
          <DatePicker
            selected={selectedDate}
            onChange={(date) => {
              setSelectedDate(date);
              setPage(1);
              setSelectedIti("all");
              setSelectedTrade("all");
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            maxDate={new Date()}
            onKeyDown={(e) => e.preventDefault()}
            wrapperClassName="w-full"
            className="w-full text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary/30 focus:border-primary outline-none transition-all bg-white hover:border-gray-300 h-[36px] px-3 py-1.5 text-gray-700 cursor-pointer caret-transparent"
          />
        </div>
      ),
    },
    {
      type: "select",
      label: "ITI",
      value: selectedIti,
      options: itiOptions,
      onChange: (val) => {
        setSelectedIti(val);
        setPage(1);
      },
    },
    {
      type: "select",
      label: "Trade",
      value: selectedTrade,
      options: tradeOptions,
      onChange: (val) => {
        setSelectedTrade(val);
        setPage(1);
      },
    },
  ];

  const paginationConfig = {
    page,
    limit,
    totalRecords,
    totalPages,
    onPageChange: (newPage) => setPage(newPage),
    onLimitChange: (newLimit) => {
      setLimit(newLimit);
      setPage(1);
    },
  };

  const columns = [
    {
      header: "Month",
      render: (row) => row?.monthLabel || "-",
    },
    {
      header: "ITI",
      render: (row) => row?.iti_name || "-",
    },
    {
      header: "Trade",
      render: (row) => row?.trade_name || "-",
    },
    {
      header: "Document",
      render: (row) =>
        row?.s3_url ? (
          <button
            type="button"
            onClick={() => window.open(row.s3_url, "_blank", "noopener,noreferrer")}
            className="text-sm font-semibold text-primary hover:underline"
          >
            Submitted Document
          </button>
        ) : (
          <span className="text-sm text-gray-400">Pending</span>
        ),
    },
  ];

  return (
    <AdminLayout>
      <GenericDataLayout
        title="Approved Attendance"
        subtitle={user?.industry?.industry_name || ""}
        filters={filtersConfig}
        onResetFilters={handleClearFilters}
        pagination={paginationConfig}
        isLoading={loading}
        actions={[]}
      >
        <GenericTable
          columns={columns}
          data={rows}
          pagination={paginationConfig}
        />
      </GenericDataLayout>
    </AdminLayout>
  );
};

export default ApprovedAttendance;
